/** Phase E (xinyi package): Taipei 101 district, Xinyi, Elephant Mountain, Taipei Main Station area. Lines marked “English practice” stay in English in every locale. */
export const enXinyi = {
  xinyi: {
    mapNames: {} as Record<string, string>,
    zones: {
      xy_xiangshan: 'Elephant Mountain',
      xy_viewpoint: 'Elephant Mountain viewpoint',
      xy_sysHall: 'Sun Yat-sen Memorial Hall',
      xy_avenue: 'Zhongxiao East Road',
      xy_arcades: 'Shop arcades',
      xy_stationFront: 'Station front',
      xy_village: 'Four Four South Village',
      xy_xinyi: 'Xinyi shopping district',
    },
    objects: {
      xy_taxi: 'Taxi stand',
      xy_sysHall: 'Sun Yat-sen Memorial Hall',
      xy_cuihu: 'Cuihu Pond',
      xy_mrtSys: 'MRT · Sun Yat-sen Memorial Hall',
      xy_mall101: 'Taipei 101 Mall',
      xy_love: 'The “愛” sculpture',
      xy_fireworks: 'New Year fireworks board',
      xy_conv: 'Good Neighbor Mart',
      xy_tea: 'Bubble Bear tea shop',
      xy_pharmacy: 'Pharmacy',
      xy_trash: 'Garbage truck stop',
      xy_shrine: 'Earth God shrine',
      xy_village: 'Four Four South Village',
      xy_bagel: 'Good Slope café',
      xy_market1: 'Market stall: soap and baskets',
      xy_market2: 'Market stall: little plants',
      xy_market3: 'Market stall: postcards',
      xy_dept: 'Galaxy department store',
      xy_books: 'Book Tree bookstore',
      xy_screen: 'Moonlight Plaza big screen',
      xy_stage: 'Street performance spot',
      xy_mrt101: 'MRT · Taipei 101 / World Trade Center',
      xy_trailhead: 'Elephant Mountain trailhead',
      xy_rocks: 'Six Giant Rocks',
      xy_viewpoint: 'Elephant Mountain viewpoint',
      xy_easycard: 'EasyCard top-up machine',
      xy_priority: 'Priority seats',
    },
    npc: {
      xy_office1: 'Office worker',
      xy_office2: 'Office worker',
      xy_tourDad: 'Tourist dad',
      xy_tourKid: 'Tourist girl',
      xy_grandma: 'Grandma Lin',
      xy_hiker: 'Mountain hiker',
      xy_clerk: 'Bookstore clerk',
      xy_teaSeller: 'Bubble-tea seller',
      xy_trashLady: 'Neighbour',
      xy_vendor: 'Market crafter',
      xy_taichi: 'Tai chi grandpa',
      xy_taxiDriver: 'Taxi driver',
      xy_performer: 'Balloon artist',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    common: {
      quiz: '📝 English quiz',
      right: '✅ Well done!',
      wrong: '❌ Not quite. Try again!',
      back: 'Maybe later',
    },
    welcome: 'Welcome to Taipei! Taipei 101 is east, past the Memorial Hall. Elephant Mountain is behind it.',

    sysHall: {
      text: 'Sun Yat-sen Memorial Hall honours Dr. Sun Yat-sen, the founding father of the Republic of China. Look at the golden-yellow roof: its corners sweep up like the wings of a bird. Inside, honour guards stand still beside a giant bronze statue, and they change every hour.',
      watch: '💂 Watch the guards',
      watched: 'Click, click! The guards march in perfect step, then stand as still as statues.',
      q: 'English practice: “What colour is the roof of the Memorial Hall?”', // English practice
      a: ['It is yellow.', 'It is green.', 'It is black.'], // English practice
      ok: '✅ Yes! Yellow glazed tiles, shining in the sun.',
    },
    cuihu: {
      text: 'Cuihu means “green lake”. Koi fish in red, white and gold swim between the lotus leaves, and turtles sunbathe on the rocks.',
      look: '🐟 Watch the fish',
      say: 'Look! The fish are so colourful!', // English practice
      looked: 'A big orange koi opens its mouth at you. Blub!',
    },
    mall: {
      text: 'The Taipei 101 Mall fills the first five floors of the tower: fashion, a supermarket and a big food court.',
      q: 'English practice: you ask “Excuse me, where is the food court?” The guard answers. Which answer makes sense?', // English practice
      a: ['It is on the basement level, B1. Take the escalator down.', 'It is in my pocket.', 'It is on the moon, next to the cat.'], // English practice
      ok: '✅ Right! Food courts in Taipei are usually in the basement.',
      browse: '👀 Window-shopping',
      browsed: 'Shiny shops and cold air conditioning. You only look… and it is fun!',
    },
    love: {
      text: 'A giant red sculpture of the character 愛 (“love”) stands on the plaza. Everyone takes a photo here!',
      ask: '📸 Ask someone to take your photo',
      howAsk: 'How do you ask politely?',
      ok: 'Excuse me, could you take a photo of me, please?', // English practice
      bad: ['Photo. Now. You.', 'Give me your camera.'], // English practice
      done: '“Sure! Say cheese!” Click! A lovely photo with a big red 愛.',
    },
    fireworks: {
      text: 'Every New Year’s Eve, at midnight, fireworks shoot out of every floor of Taipei 101 for about five minutes. Hundreds of thousands of people count down together in Xinyi.',
      q: 'English practice: “When do the fireworks start?”', // English practice
      a: ['At midnight, when the new year begins.', 'At lunchtime on Tuesdays.', 'Every morning at seven.'], // English practice
      ok: '✅ Right! Ten, nine, eight… Happy New Year!',
      countdown: 'Ten, nine, eight, seven, six, five, four, three, two, one… Happy New Year!', // English practice
    },
    conv: {
      text: 'Ding-dong! The door chime of the Good Neighbor Mart. Convenience stores in Taiwan sell snacks, pay bills, print documents and are open 24 hours.',
      snack: '🥚 Buy a tea egg and water',
      bag: '🛍️ “Do you need a bag?”',
      food: { vendor: 'Welcome! Anything else?', ok: 'One tea egg and a bottle of water, please.', bad: ['Egg. Water. Go.', 'I want the whole shop for free.'], done: 'A warm tea egg that smells of soy sauce and star anise, and cold water. Perfect!', price: 25 }, // English practice
      bagQ: 'English practice: the clerk asks “Do you need a bag?” You have your own bag. What do you say?', // English practice
      bagA: ['No, thanks. I have one.', 'Bag is me.', 'Yes, I need ten bags for one egg.'], // English practice
      bagOk: '✅ Great! Plastic bags cost money in Taiwan, so people bring their own.',
    },
    tea: {
      text: 'Bubble Bear 泡泡熊: pearl milk tea was invented in Taiwan! You choose the sugar (全糖 full, 半糖 half, 微糖 a little, 無糖 none) and the ice (少冰 less ice, 去冰 no ice).',
      menu: 'What would you like?',
      milk: '🧋 Pearl milk tea',
      green: '🍵 Jasmine green tea',
      milkOrder: { vendor: 'One pearl milk tea! How much sugar and ice?', ok: 'Half sugar, less ice, please.', bad: ['Sugar sugar ice ice!', 'Give me all the sugar and no cup.'], done: 'Shake, shake, pop! The straw goes in. Chewy pearls and creamy tea, not too sweet!', price: 50 }, // English practice
      greenOrder: { vendor: 'Jasmine green tea! Sugar and ice level?', ok: 'No sugar and no ice, please.', bad: ['Tea. Cold. Sugar? Maybe.', 'I want a hot ice tea.'], done: 'Light, flowery and fresh. A local favourite!', price: 35 }, // English practice
    },
    pharmacy: {
      text: '藥局 means pharmacy. The pharmacist can help with small problems like a cold or a headache.',
      q: 'English practice: you have a headache. What do you ask?', // English practice
      a: ['Excuse me, do you have something for a headache?', 'My head is a banana.', 'Give me all the medicine.'], // English practice
      ok: '✅ Perfect. The pharmacist smiles: “Drink water and get some rest!”',
    },
    trash: {
      text: 'Taipei has very few street bins. Every evening the yellow garbage truck comes playing music — these days “A Maiden’s Prayer” by Bądarzewska (some towns still play Beethoven’s “Für Elise”) — and everyone runs out with their bags. Recycling goes into a second truck.',
      here: '🎵 The garbage truck is here! Neighbours hurry out with their bags.',
      notYet: 'The truck comes here at about 8 p.m.',
      q: 'English practice: what do you say when you go out with the bags?', // English practice
      a: ['I am taking out the trash!', 'I am taking out the dog’s homework!', 'I am eating the trash!'], // English practice
      ok: '✅ Yes! And don’t forget to sort the recycling.',
      toast: '🎵 Ta-da-da-da… “A Maiden’s Prayer”! The garbage truck is coming down the street.',
    },
    shrine: {
      text: 'A little shrine for Tudigong (土地公), the Earth God who protects the neighbourhood. People bow with incense and leave fruit, tea or cookies.',
      bow: '🙏 Bow three times',
      wish: 'Please keep my family safe and healthy.', // English practice
      bowed: 'You bow three times. The incense smoke curls up into the banyan tree.',
    },
    village: {
      text: 'Four Four South Village (四四南村) was built in 1948 for the families of workers at the 44th arsenal. The low grey-brick houses are now a little museum, shops and a café — right below Taipei 101: old and new side by side.',
      q: 'English practice: “Who lived in these houses in the old days?”', // English practice
      a: ['Military families.', 'Pirates.', 'Astronauts.'], // English practice
      ok: '✅ Right! Military dependents’ villages (眷村) were all over Taiwan.',
      market: 'Every Sunday the lawn becomes the Simple Market: handmade crafts, plants and snacks.',
      noMarket: 'The Simple Market is only on Sundays. Come back then!',
    },
    bagel: { vendor: 'Hi! Our bagels are fresh from the oven. What would you like?', ok: 'Could I have a sesame bagel and a cup of tea, please?', bad: ['Bagel! Hurry!', 'I want a bagel made of tea.'], done: 'A warm, chewy sesame bagel and tea in an old village house. So cosy!', price: 45 }, // English practice
    market: {
      ask: 'How much is this, please?', // English practice
      stalls: {
        xy_market1: { text: 'Handmade soap that smells of grapefruit, and little woven baskets.', answer: 'It is thirty dollars.', price: 30 }, // English practice
        xy_market2: { text: 'Tiny succulents in teacups and pots made from old tins.', answer: 'It is twenty dollars.', price: 20 }, // English practice
        xy_market3: { text: 'Hand-drawn postcards of Taipei 101, the MRT and street cats.', answer: 'It is fifteen dollars.', price: 15 }, // English practice
      },
      buy: (n: number) => `🛍️ Buy it (${n} coins)`,
      bought: 'You bought something handmade. The crafter wraps it in old newspaper with a smile.',
      poor: 'You don’t have enough coins.',
    },
    dept: {
      text: 'Galaxy department store: ten floors of shops, connected to its neighbours by skybridges. In autumn every store has a huge anniversary sale (週年慶).',
      q: 'English practice: you like a T-shirt. What do you ask the shop assistant?', // English practice
      a: ['Excuse me, can I try this on?', 'T-shirt! Me! Now!', 'How old is this shirt?'], // English practice
      ok: '✅ “Of course! The fitting room is over there.”',
    },
    books: {
      text: 'Book Tree 書林: a famous bookstore where people sit on the floor and read for hours. Many floors of books, music, stationery and a café.',
      read: '📖 Read for a while',
      readDone: 'You read an English picture book about a cat who rides the MRT. Time flies!',
      askClerk: '🗣️ Ask about English books',
      clerkSays: 'The clerk says: “English books? They are on the second floor, next to the stairs.”', // English practice
      q: 'Where are the English books?',
      a: ['On the second floor, next to the stairs.', 'In the basement, next to the café.', 'On the roof.'], // English practice
      ok: '✅ Good listening! Up the stairs you go.',
      closed: 'Book Tree is closed now. It opens at 10 a.m.',
    },
    screen: {
      text: 'The giant screen of Moonlight Plaza shows ads, music videos and city facts.',
      q: 'English practice: the screen asks “How tall is Taipei 101?”', // English practice
      a: ['It is 508 metres tall.', 'It is 50 metres tall.', 'It is 5,000 metres tall.'], // English practice
      ok: '✅ Right! It was the tallest building in the world from 2004 to 2010.',
    },
    stage: {
      text: 'A street-performance spot. Performers in Taipei need a street-artist licence, and they play here every evening.',
      watch: '🎶 Watch the show',
      watched: 'Music, balloons and clapping. The crowd cheers!',
      tip: '🪙 Tip 5 coins',
      thanks: 'That was amazing! Thank you!', // English practice
      empty: 'Nobody is performing right now. Come back in the afternoon or evening.',
    },
    trail: {
      text: 'Elephant Mountain trail (象山步道): about 20 minutes of steep stone stairs to the viewpoint over Taipei 101. Bring water and take a break when you need one!',
      q: 'English practice: “How long does it take to get to the top?”', // English practice
      a: ['About twenty minutes.', 'About twenty days.', 'Twenty seconds.'], // English practice
      ok: '✅ Right! Let’s go: one step at a time.',
      tired: 'Phew, so many stairs! (−energy)',
    },
    rocks: {
      text: 'The Six Giant Rocks (六巨石): six huge boulders near the top of Elephant Mountain. Hikers climb them for a famous photo.',
      climb: '🧗 Climb a rock',
      careful: 'Be careful! Hold on tight.', // English practice
      climbed: 'You climb up carefully and wave from the top. The city sparkles below!',
    },
    viewpoint: {
      text: 'This is the classic view: Taipei 101 rising above the whole city.',
      shoot: '📸 Take the photo',
      say: 'What a view! Taipei 101 looks so tall from here.', // English practice
      title: '📸 Elephant Mountain',
      caption: 'Your photo of Taipei 101 from Elephant Mountain.',
      tap: 'Tap to close',
    },
    taxi: {
      text: 'Yellow taxis are everywhere in Taipei. The driver asks: “Where to?”', // English practice
      to: {
        station: 'To Taipei Main Station, please.',
        tower: 'To Taipei 101, please.',
        village: 'To Four Four South Village, please.',
        trail: 'To the Elephant Mountain trailhead, please.',
      }, // English practice
      price: (n: number) => `${n} coins`,
      poor: 'You need more coins for the taxi.',
      ride: '🚕 Off you go! “Please fasten your seat belt.”', // English practice
    },
    easycard: {
      text: 'The EasyCard machine: put your card on the reader, choose an amount, then insert money. You can use the EasyCard on the MRT, buses, YouBikes and in convenience stores.',
      top: '💳 Top up my card',
      q: 'English practice: the screen says “Please place your card on the reader.” What do you do first?', // English practice
      a: ['I put my card on the reader.', 'I put my card in my mouth.', 'I shout at the machine.'], // English practice
      q2: 'English practice: “How much would you like to add?”', // English practice
      a2: ['One hundred dollars, please.', 'My name is one hundred.', 'Yes, I would like a train.'], // English practice
      ok: '✅ Beep! Your card is topped up. The balance shows on the screen.',
    },
    priority: {
      text: 'The dark blue seats are priority seats (博愛座). Anyone may sit there, but you give them up to people who need them more.',
      q: 'English practice: an old man gets on the train. What do you say?', // English practice
      a: ['Please, take my seat.', 'This is my seat forever.', 'Go away, please.'], // English practice
      ok: '✅ Very kind! He says: “Thank you, young friend.”',
    },

    talk: {
      guideTip: 'Tip: the yellow taxis wait by the station. Taipei 101 is east, and Elephant Mountain is right behind it!',
      office1: [
        'Lunch break! The food court in the basement is so crowded at noon.',
        'I work on the 35th floor of a tower over there. My elevator is not as fast as 101’s!',
        'English practice: “I have a meeting at two.” “See you later!”', // English practice
      ],
      office2: [
        'I take the MRT every day. I tap my EasyCard and go!',
        'After work I like to walk across the skybridges. You never have to cross a road!',
        'English practice: “Excuse me, is this seat taken?” “No, go ahead.”', // English practice
      ],
      tourAsk: 'Excuse me! We are lost. How do we get to Taipei 101?', // English practice
      tourQ: 'How do you give directions?',
      tourA: ['Go straight along this road, then look for the tallest tower. You can’t miss it!', 'Taipei 101 is in my bag.', 'Turn left, turn left, turn left, turn left.'], // English practice
      tourOk: '“Thank you so much! You are very kind.” The family waves and walks east.',
      tourKid: ['I like the scooters! There are so many!', 'My dad says we can have bubble tea later!', 'English practice: “What is your name?” “My name is Lily. Nice to meet you!”'], // English practice
      grandmaShrine: [
        'I come to see Tudigong every morning. He keeps our street safe.',
        'Bow three times, like this. Gently, gently.',
        'English practice: “Good morning, Grandma!” — say it to me!', // English practice
      ],
      grandmaMarket: [
        'Oh, the Sunday market! I always buy a little plant.',
        'I grew up in a village like this. We shared everything with the neighbours.',
      ],
      hiker: [
        'Keep going! The view at the top is worth every step.',
        'English practice: “I am out of breath!” “Let’s take a short break.”', // English practice
        'At sunset the 101 lights up. Stay for the night view!',
      ],
      clerk: [
        'Welcome to Book Tree. You can read anything you like, take your time.',
        'Our English section is on the second floor, next to the stairs.',
        'English practice: “Could you recommend a good book?”', // English practice
      ],
      trashLady: [
        'Hurry, hurry! When you hear “A Maiden’s Prayer”, the truck is coming!',
        'Trash in the yellow truck, recycling in the white one.',
        'English practice: “Don’t forget to take out the trash!”', // English practice
      ],
      vendor: [
        'Everything here is handmade by me!',
        'English practice: “How much is this?” “It is twenty dollars.”', // English practice
      ],
      taichi: [
        'Slowly… breathe in… breathe out. Tai chi keeps me young!',
        'I am eighty-two years old. I come here every morning.',
        'English practice: “Breathe in. Breathe out. Relax.”', // English practice
      ],
      taxiDriver: [
        'Where to? I know every street in Taipei!',
        'English practice: “Please fasten your seat belt.”', // English practice
      ],
      performer: [
        'A balloon for you! Do you want a dog or a giraffe?',
        'English practice: “That was amazing!” — that’s what the crowd says.', // English practice
      ],
      teaSeller: 'Welcome to Bubble Bear! How much sugar? How much ice?',
    },
  },
};
